/**
 * Spec Phase 4 Slice 4D — SectionRow.
 * Phase 11 Slice 11D — Generate-Everything overlay.
 * BUG-033 — drafter exclude/include + drag-to-reorder affordances.
 * BUG-048 — drafter inline rename via double-click / pencil icon
 *           + jurisdiction-style preset dropdown + Reset to
 *           default. Auto-generated sections cannot be renamed
 *           (export contract relies on their fixed headings).
 *
 * Single section row in the left rail. Visual format per brief §4:
 *   [⠿] [bar] 07  Detailed description of drawings   editing  [✏][✕]
 *
 * - ⠿: drag handle (cursor: grab) — drag-and-drop reorders sections
 *      via the parent SectionNav's HTML5 DnD listeners.
 * - bar: 4px coloured by ui_state (.s6-sec-{done|warn|active|empty|locked})
 * - 07: 2-digit sequence number, monospace
 * - Title: section name (custom_title ?? title); subtle italic +
 *   "•" indicator on rows that carry a custom override.
 * - Meta: meta_text from the section row (e.g. "340 w" / "2 issues").
 * - ✏: rename pencil (hover-revealed; hidden on auto-generated).
 * - ✕: exclude/include toggle (hidden on auto-generated).
 *
 * In rename mode the title cell becomes an `<input>` with a
 * preset dropdown + character counter + Reset/Save controls.
 * Enter saves; Escape cancels; blur saves.
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Loader2, GripVertical, X, Eye, Pencil, RotateCcw, Check,
} from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import type { SpecSectionRow as SectionRowData } from '@/api/client';
import { presetsForKind } from './sectionRenamePresets';

const _MAX_TITLE = 120;

interface Props {
  section: SectionRowData;
  isActive: boolean;
  onClick: () => void;
  /** BUG-033 — non-null when the section can be excluded (i.e.
   * not auto-generated). Calling it toggles is_excluded server-
   * side and the rail re-renders. */
  onToggleExcluded?: ((nextExcluded: boolean) => void) | null;
  /** BUG-048 — invoked when the drafter saves a rename via
   * Enter / blur / preset / Reset. Pass ``null`` to clear the
   * override and revert to the seed default. Null (the prop,
   * not the value) signals "not renamable" — auto-generated
   * sections pass null and the pencil + dbl-click handlers stay
   * inert. */
  onRename?: ((next: string | null) => Promise<boolean> | boolean) | null;
  /** BUG-033 — pointerdown on the drag handle starts an HTML5
   * drag. SectionNav binds the parent dnd events; this prop
   * surfaces the handle's draggability so the parent can route
   * the event. */
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void;
  isDropTarget?: boolean;
}

export default function SectionRow({
  section,
  isActive,
  onClick,
  onToggleExcluded,
  onRename,
  draggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDropTarget = false,
}: Props): JSX.Element {
  const geStatus = useSpecStore((s) => s.generateEverythingStatus);
  const cachedParagraphs = useSpecStore(
    (s) => s.paragraphsBySection[section.id],
  );
  // Live "generating…" + "⚠ failed" overlays remain driven by
  // the GE-run cache — that IS the right source for ephemeral
  // job state. The persistent "✓ generated" badge moves to
  // actual paragraph state below so badge + content area stay
  // consistent across reloads (the previous wiring kept showing
  // "✓ generated" off the GE cache even after paragraphs were
  // deleted or the GE crashed before persist).
  let geOverlay: JSX.Element | null = null;
  if (geStatus && (geStatus.status === 'queued'
                   || geStatus.status === 'running')) {
    if (geStatus.progress.current_section === section.section_kind) {
      geOverlay = (
        <span className="inline-flex items-center gap-1 font-mono text-[10px] text-blue-700">
          <Loader2 className="h-3 w-3 animate-spin" /> generating…
        </span>
      );
    }
  }
  if (!geOverlay && geStatus) {
    const result = geStatus.section_results.find(
      (r) => r.section_kind === section.section_kind,
    );
    if (result?.status === 'failed') {
      geOverlay = (
        <span className="font-mono text-[10px] text-rose-700">
          ⚠ failed
        </span>
      );
    }
  }
  // Persistent "✓ generated" badge — derived from server-truth
  // paragraph_count (primary) with a cached-paragraphs fallback
  // (secondary, in case the section row's count is briefly
  // stale while paragraphsBySection has already been refreshed).
  if (!geOverlay) {
    const hasParagraphs = (section.paragraph_count ?? 0) > 0;
    const hasCachedParagraphs = Array.isArray(cachedParagraphs)
      && cachedParagraphs.length > 0;
    if (hasParagraphs || hasCachedParagraphs) {
      geOverlay = (
        <span className="font-mono text-[10px] text-emerald-700">
          ✓ generated
        </span>
      );
    }
  }

  const isExcluded = !!section.is_excluded;
  const seedTitle = section.title || '';
  const customTitle = section.custom_title || null;
  const displayTitle = (customTitle && customTitle.trim()) || seedTitle;
  const isRenamed = !!(customTitle && customTitle.trim()
    && customTitle.trim() !== seedTitle);
  const cls = [
    's6-sec',
    `s6-sec-${section.ui_state}`,
    'group flex items-start gap-1 w-full px-1.5 py-2 transition-colors',
    isActive ? 'bg-blue-50' : 'hover:bg-zinc-50',
    isDropTarget ? 'ring-1 ring-inset ring-[#002FA7]' : '',
  ].join(' ');

  // ── Inline-rename state ────────────────────────────────────────
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [savedFlash, setSavedFlash] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cancelledRef = useRef<boolean>(false);

  const canRename = !!onRename;

  const startEdit = useCallback(() => {
    if (!canRename) return;
    setError(null);
    setDraft((customTitle && customTitle.trim()) || seedTitle);
    cancelledRef.current = false;
    setEditing(true);
  }, [canRename, customTitle, seedTitle]);

  // Focus + select on entering edit mode.
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = useCallback(async (
    nextTitle: string | null,
  ): Promise<void> => {
    if (!onRename || saving) return;
    setSaving(true);
    setError(null);
    try {
      const ok = await onRename(nextTitle);
      if (ok !== false) {
        setEditing(false);
        setSavedFlash(true);
        window.setTimeout(() => setSavedFlash(false), 1000);
      } else {
        setError('Save failed.');
      }
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'Save failed.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [onRename, saving]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = draft.trim();
      if (!trimmed) {
        setError('Name cannot be empty. Use Reset to revert.');
        return;
      }
      void commit(trimmed === seedTitle ? null : trimmed);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelledRef.current = true;
      setEditing(false);
      setError(null);
    }
  }, [commit, draft, seedTitle]);

  const onBlur = useCallback(() => {
    // Allow Escape's cancel to take precedence by deferring
    // the blur-save to the next tick so cancelledRef.current
    // (set by Escape) wins.
    window.setTimeout(() => {
      if (cancelledRef.current) {
        cancelledRef.current = false;
        return;
      }
      if (!editing) return;
      const trimmed = draft.trim();
      if (!trimmed) {
        // Empty on blur → cancel rather than error-loop.
        setEditing(false);
        return;
      }
      void commit(trimmed === seedTitle ? null : trimmed);
    }, 0);
  }, [commit, draft, editing, seedTitle]);

  const presets = presetsForKind(section.section_kind);
  const remaining = _MAX_TITLE - draft.length;

  return (
    <div
      className={cls}
      data-testid={`spec-section-row-${section.section_kind}`}
      data-section-id={section.id}
      draggable={draggable && !editing}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {draggable && (
        <span
          className="mt-0.5 cursor-grab text-zinc-300 hover:text-zinc-500 select-none"
          aria-label="Drag to reorder"
          title="Drag to reorder"
          data-testid={`spec-section-handle-${section.section_kind}`}
        >
          <GripVertical className="h-3.5 w-3.5" aria-hidden />
        </span>
      )}
      {!editing && (
        <button
          type="button"
          className="flex flex-1 items-center gap-2 truncate text-left"
          onClick={onClick}
          onDoubleClick={(e) => {
            if (!canRename) return;
            e.stopPropagation();
            startEdit();
          }}
          aria-current={isActive ? 'true' : undefined}
          aria-label={`${displayTitle}, ${section.meta_text}`}
        >
          <span className="font-mono text-[11px] text-zinc-400 w-6 shrink-0">
            {String(section.sequence_num).padStart(2, '0')}
          </span>
          <span
            className={[
              'flex-1 truncate text-sm',
              isExcluded ? 'line-through text-zinc-400' : 'text-zinc-800',
              isRenamed ? 'italic' : '',
            ].join(' ')}
            title={isRenamed
              ? `Custom name (default: ${seedTitle})`
              : displayTitle}
          >
            {displayTitle}
            {isRenamed && (
              <span
                className="ml-1 text-[10px] text-[#002FA7]"
                aria-label="Custom name"
              >
                •
              </span>
            )}
            {savedFlash && (
              <span
                className="ml-1 inline-flex items-center text-[10px] text-emerald-700"
                role="status"
                aria-live="polite"
              >
                <Check className="h-2.5 w-2.5" aria-hidden /> saved
              </span>
            )}
          </span>
          <span className="font-mono text-[10px] text-zinc-500 shrink-0">
            {isExcluded
              ? <span className="rounded-sm bg-zinc-100 px-1 text-[10px] text-zinc-500">excluded</span>
              : (geOverlay ?? section.meta_text)}
          </span>
        </button>
      )}
      {editing && (
        <div
          className="flex flex-1 flex-col gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1">
            <span className="font-mono text-[11px] text-zinc-400 w-6 shrink-0">
              {String(section.sequence_num).padStart(2, '0')}
            </span>
            <input
              ref={inputRef}
              type="text"
              className="flex-1 rounded-sm border border-[#002FA7] bg-white px-1.5 py-0.5 text-sm text-zinc-900 focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
              value={draft}
              maxLength={_MAX_TITLE}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              data-testid={`spec-section-rename-input-${section.section_kind}`}
              aria-label={`Rename section ${seedTitle}`}
            />
          </div>
          <div className="flex items-center justify-between gap-2 pl-7 text-[10px]">
            <div className="flex items-center gap-2">
              {presets.length > 0 && (
                <select
                  className="rounded-sm border border-zinc-300 bg-white px-1 py-0.5 text-[10px] text-zinc-700"
                  value=""
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) setDraft(v);
                  }}
                  // Keep mousedown alive — closing the select via
                  // selection re-focuses the input on the next
                  // tick; we use a ref-stamped dance to avoid the
                  // blur-save firing in between.
                  onMouseDown={() => { cancelledRef.current = true; }}
                  onMouseUp={() => {
                    cancelledRef.current = false;
                    inputRef.current?.focus();
                  }}
                  data-testid={`spec-section-rename-presets-${section.section_kind}`}
                  aria-label="Preset names for this section"
                >
                  <option value="">Use preset…</option>
                  {presets.map((p) => (
                    <option key={p.label} value={p.label}>
                      {p.label}{p.hint ? ` — ${p.hint}` : ''}
                    </option>
                  ))}
                </select>
              )}
              {customTitle && (
                <button
                  type="button"
                  className="inline-flex items-center gap-0.5 text-[#002FA7] hover:text-[#001f6f]"
                  onClick={() => {
                    cancelledRef.current = true;
                    void commit(null);
                  }}
                  data-testid={`spec-section-rename-reset-${section.section_kind}`}
                  title={`Reset to default: ${seedTitle}`}
                >
                  <RotateCcw className="h-3 w-3" aria-hidden />
                  Reset to default
                </button>
              )}
            </div>
            <span
              className={[
                'tabular-nums',
                remaining < 10 ? 'text-rose-600' : 'text-zinc-400',
              ].join(' ')}
              data-testid={`spec-section-rename-counter-${section.section_kind}`}
            >
              {remaining}
            </span>
          </div>
          {saving && (
            <p className="pl-7 text-[10px] text-zinc-500">Saving…</p>
          )}
          {error && (
            <p className="pl-7 text-[10px] text-rose-700" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
      {/* Hover-revealed pencil — hidden on auto-generated and
          while editing. Stops propagation so clicking it doesn't
          activate the section. */}
      {canRename && !editing && (
        <button
          type="button"
          className="ml-1 shrink-0 text-zinc-300 opacity-0 hover:text-zinc-700 group-hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            startEdit();
          }}
          aria-label={`Rename ${displayTitle}`}
          title="Rename section (or double-click the name)"
          data-testid={`spec-section-rename-${section.section_kind}`}
        >
          <Pencil className="h-3 w-3" aria-hidden />
        </button>
      )}
      {onToggleExcluded && !editing && (
        <button
          type="button"
          className="ml-1 shrink-0 text-zinc-300 hover:text-zinc-700"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExcluded(!isExcluded);
          }}
          aria-label={isExcluded
            ? `Re-include ${displayTitle} in exports`
            : `Exclude ${displayTitle} from exports`}
          aria-pressed={isExcluded}
          title={isExcluded
            ? 'Re-include this section in exports.'
            : 'Exclude this section from DOCX/PDF exports (drafter override; row stays in the DB).'}
          data-testid={`spec-section-exclude-${section.section_kind}`}
        >
          {isExcluded
            ? <Eye className="h-3.5 w-3.5" aria-hidden />
            : <X className="h-3.5 w-3.5" aria-hidden />}
        </button>
      )}
    </div>
  );
}
